import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import api, { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Building2, ChevronLeft, Pencil, Trash2, UserPlus, Users, AlertTriangle,
  CalendarClock, ShieldCheck, Mail, Phone, MapPin, Globe, FileText, Search, UserMinus, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import MemberProfile from "@/components/MemberProfile";
import { SearchableSelect } from "@/components/SearchableSelect";

const STATUS_META = {
  expired:        { label: "Expired",       className: "bg-rose-100 text-rose-700 border-rose-200" },
  expiring_soon:  { label: "Expiring soon", className: "bg-amber-100 text-amber-700 border-amber-200" },
  active:         { label: "Active",        className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  no_expiry:      { label: "No expiry",     className: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function BusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin";

  const [business, setBusiness] = useState(null);
  const [learners, setLearners] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter: "all" | "expiring_soon" | "expired" | "learners"
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Profile dialog
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileMemberId, setProfileMemberId] = useState(null);
  // Collapsed-by-default per-learner groups so a learner with 20 quals
  // doesn't bloat the table — click the row to expand.
  const [openLearners, setOpenLearners] = useState({});
  const toggleLearner = (mid) => setOpenLearners(prev => ({ ...prev, [mid]: !prev[mid] }));

  // Edit business dialog
  const [editBizOpen, setEditBizOpen] = useState(false);
  const [bizForm, setBizForm] = useState({
    name: "", contact_name: "", contact_email: "", contact_phone: "",
    address: "", website: "", notes: "",
  });

  // Delete business confirm
  const [deleteBizOpen, setDeleteBizOpen] = useState(false);

  // Learner dialogs
  const emptyLearner = {
    name: "", email: "", phone: "", company: "",
    address_line1: "", address_line2: "", city: "", county: "", postcode: "", country: "United Kingdom",
    notes: "",
  };
  const [learnerForm, setLearnerForm] = useState(emptyLearner);
  const [learnerDialogOpen, setLearnerDialogOpen] = useState(false);
  const [learnerMode, setLearnerMode] = useState("existing"); // "existing" | "new"
  const [allLearners, setAllLearners] = useState([]);
  const [existingPickId, setExistingPickId] = useState("");
  const [editingLearner, setEditingLearner] = useState(null);
  const [deleteLearner, setDeleteLearner] = useState(null);
  const [unlinkLearner, setUnlinkLearner] = useState(null);

  useDocumentTitle(business?.name ? `${business.name} · Business` : "Business", "");

  const load = useCallback(async () => {
    try {
      const [b, l, c] = await Promise.all([
        api.get(`/businesses/${id}`),
        api.get(`/businesses/${id}/learners`),
        api.get(`/businesses/${id}/certificates`),
      ]);
      setBusiness(b.data);
      setLearners(l.data);
      setCerts(c.data);
    } catch (err) {
      if (!isAuthError(err)) {
        toast.error("Failed to load business");
        if (err.response?.status === 404) navigate("/businesses");
      }
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const certsByStatus = useMemo(() => {
    const m = { expired: [], expiring_soon: [], active: [], no_expiry: [] };
    certs.forEach(c => m[c.status]?.push(c));
    return m;
  }, [certs]);

  // Build a "no cert" pseudo-row for learners with zero certificates so they're still visible
  const learnerRows = useMemo(() => {
    const haveCerts = new Set(certs.map(c => c.member_id));
    const orphans = learners.filter(l => !haveCerts.has(l.id)).map(l => ({
      id: `orphan-${l.id}`,
      member_id: l.id,
      member_name: l.name,
      member_email: l.email,
      qualification_name: null,
      issue_date: null,
      expiry_date: null,
      status: "no_expiry",
      days_until_expiry: null,
      _orphan: true,
    }));
    return [...certs, ...orphans];
  }, [certs, learners]);

  const visibleRows = useMemo(() => {
    let rows = learnerRows;
    if (filter === "expired") rows = rows.filter(r => r.status === "expired");
    else if (filter === "expiring_soon") rows = rows.filter(r => r.status === "expiring_soon");
    else if (filter === "active") rows = rows.filter(r => r.status === "active");
    const s = search.toLowerCase().trim();
    if (s) rows = rows.filter(r =>
      (r.member_name || "").toLowerCase().includes(s) ||
      (r.member_email || "").toLowerCase().includes(s) ||
      (r.qualification_name || "").toLowerCase().includes(s)
    );
    return rows;
  }, [learnerRows, filter, search]);

  /* ---------------- Business edit / delete ---------------- */

  const openEditBiz = () => {
    if (!business) return;
    setBizForm({
      name: business.name || "",
      contact_name: business.contact_name || "",
      contact_email: business.contact_email || "",
      contact_phone: business.contact_phone || "",
      address: business.address || "",
      website: business.website || "",
      notes: business.notes || "",
    });
    setEditBizOpen(true);
  };

  const saveBiz = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/businesses/${id}`, bizForm);
      toast.success("Business updated");
      setEditBizOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDeleteBiz = async () => {
    try {
      await api.delete(`/businesses/${id}`);
      toast.success("Business deleted");
      navigate("/businesses");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  /* ---------------- Learner CRUD ---------------- */

  const openAddLearner = async () => {
    setEditingLearner(null);
    setLearnerForm(emptyLearner);
    setExistingPickId("");
    setLearnerMode("existing");
    setLearnerDialogOpen(true);
    try {
      const { data } = await api.get("/members?role=student&limit=2000");
      setAllLearners(data.members || []);
    } catch (err) {
      if (!isAuthError(err)) toast.error("Failed to load learners — try again");
    }
  };

  const attachExistingLearner = async () => {
    if (!existingPickId) return;
    try {
      await api.put(`/members/${existingPickId}`, { business_id: id });
      toast.success("Learner attached to business");
      setLearnerDialogOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEditLearner = (l) => {
    setEditingLearner(l);
    setLearnerMode("new");  // edit always uses the form view
    setLearnerForm({
      name: l.name || "", email: l.email || "", phone: l.phone || "", company: l.company || "",
      address_line1: l.address_line1 || "", address_line2: l.address_line2 || "",
      city: l.city || "", county: l.county || "", postcode: l.postcode || "",
      country: l.country || "United Kingdom", notes: l.notes || "",
    });
    setLearnerDialogOpen(true);
  };

  const saveLearner = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...learnerForm };
      if (!payload.email || !payload.email.trim()) delete payload.email;
      if (editingLearner) {
        await api.put(`/members/${editingLearner.id}`, payload);
        toast.success("Learner updated");
      } else {
        await api.post("/members", { ...payload, role: "student", business_id: id });
        toast.success("Learner added");
      }
      setLearnerDialogOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmDeleteLearner = async () => {
    try {
      await api.delete(`/members/${deleteLearner.id}`);
      toast.success("Learner deleted");
      setDeleteLearner(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmUnlinkLearner = async () => {
    try {
      await api.put(`/members/${unlinkLearner.id}`, { business_id: "" });
      toast.success("Removed from business");
      setUnlinkLearner(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  /* ---------------- Render ---------------- */

  if (loading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!business) return null;

  const expiringSoonest = certsByStatus.expiring_soon.slice(0, 5);
  const expiredList = certsByStatus.expired.slice(0, 5);

  return (
    <div className="space-y-4" data-testid="business-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/businesses")}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-slate-900"
          data-testid="business-back-btn"
        >
          <ChevronLeft size={14} /> Businesses
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openEditBiz} className="h-8 text-xs" data-testid="business-edit-btn">
            <Pencil size={12} className="mr-1.5" /> Edit business
          </Button>
          {isAdmin && (
            <Button
              variant="outline" size="sm"
              onClick={() => setDeleteBizOpen(true)}
              className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              data-testid="business-delete-btn"
            >
              <Trash2 size={12} className="mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="rounded-lg border bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-slate-900 text-white p-2.5"><Building2 size={18} /></div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl font-semibold">{business.name}</h1>
            <div className="mt-1.5 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              {business.contact_name && <span className="flex items-center gap-1"><Users size={12} /> {business.contact_name}</span>}
              {business.contact_email && <span className="flex items-center gap-1"><Mail size={12} /> {business.contact_email}</span>}
              {business.contact_phone && <span className="flex items-center gap-1"><Phone size={12} /> {business.contact_phone}</span>}
              {business.address && <span className="flex items-center gap-1"><MapPin size={12} /> {business.address}</span>}
              {business.website && <span className="flex items-center gap-1"><Globe size={12} /> {business.website}</span>}
            </div>
            {business.notes && (
              <div className="mt-3 text-xs text-slate-600 flex items-start gap-1.5">
                <FileText size={12} className="mt-0.5 shrink-0 text-slate-400" />
                <span className="whitespace-pre-wrap">{business.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stat cards (clickable) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<Users size={14} />}
          label="Learners"
          value={business.learner_count}
          active={filter === "all"}
          onClick={() => setFilter("all")}
          tone="slate"
          testId="stat-all"
        />
        <StatCard
          icon={<AlertTriangle size={14} />}
          label="Expired"
          value={business.expired}
          active={filter === "expired"}
          onClick={() => setFilter("expired")}
          tone="rose"
          testId="stat-expired"
        />
        <StatCard
          icon={<CalendarClock size={14} />}
          label="Expiring soon"
          sublabel="next 60 days"
          value={business.expiring_soon}
          active={filter === "expiring_soon"}
          onClick={() => setFilter("expiring_soon")}
          tone="amber"
          testId="stat-expiring"
        />
        <StatCard
          icon={<ShieldCheck size={14} />}
          label="Active certs"
          value={business.active}
          active={filter === "active"}
          onClick={() => setFilter("active")}
          tone="emerald"
          testId="stat-active"
        />
      </div>

      {/* Follow-up hint banner */}
      {(business.expired > 0 || business.expiring_soon > 0) && filter === "all" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="followup-banner">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium mb-1">Follow-up suggested for {business.name}</div>
            <ul className="list-disc list-inside space-y-0.5">
              {expiredList.map(c => (
                <li key={c.id}>
                  <span className="font-medium">{c.member_name}</span> — {c.qualification_name} (expired {c.expiry_date ? format(parseISO(c.expiry_date), "d MMM yyyy") : ""})
                </li>
              ))}
              {expiringSoonest.map(c => (
                <li key={c.id}>
                  <span className="font-medium">{c.member_name}</span> — {c.qualification_name} (renew by {c.expiry_date ? format(parseISO(c.expiry_date), "d MMM yyyy") : ""})
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          Showing <span className="font-medium text-slate-700">{visibleRows.length}</span> {filter === "all" ? "row" : `${STATUS_META[filter]?.label.toLowerCase()} row`}{visibleRows.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search learners or quals…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs w-56"
              data-testid="business-detail-search"
            />
          </div>
          <Button size="sm" onClick={openAddLearner} className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs" data-testid="biz-add-learner-btn">
            <UserPlus size={14} className="mr-1.5" /> Add Learner
          </Button>
        </div>
      </div>

      {/* Table — grouped per learner. Each learner is one collapsible
          row showing a status summary (expired · soon · active · total);
          click to expand into their qualifications underneath. */}
      <div className="rounded-md border bg-white overflow-hidden" data-testid="business-learners-grouped">
        {visibleRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm">
            {learners.length === 0
              ? <>No learners attached yet. Click <span className="font-medium text-slate-700">Add Learner</span> to start.</>
              : <>No rows match the current filter.</>}
          </div>
        ) : (() => {
          // Group the flat visibleRows by member so a learner with
          // several certs shows up as a single collapsible group.
          const groups = new Map();
          visibleRows.forEach(r => {
            const key = r.member_id;
            if (!groups.has(key)) {
              groups.set(key, {
                member_id: r.member_id,
                member_name: r.member_name,
                member_email: r.member_email,
                rows: [],
                counts: { expired: 0, expiring_soon: 0, active: 0, no_expiry: 0 },
              });
            }
            const g = groups.get(key);
            g.rows.push(r);
            if (r.qualification_name) {  // placeholder "no cert yet" rows shouldn't count
              g.counts[r.status] = (g.counts[r.status] || 0) + 1;
            }
          });
          return (
            <div className="divide-y divide-slate-100">
              {Array.from(groups.values()).map(g => {
                const learnerDoc = learners.find(l => l.id === g.member_id);
                const open = !!openLearners[g.member_id];
                const total = g.rows.filter(r => r.qualification_name).length;
                return (
                  <div key={g.member_id} data-testid={`biz-learner-group-${g.member_id}`}>
                    <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors">
                      <button
                        type="button"
                        onClick={() => toggleLearner(g.member_id)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                        data-testid={`biz-learner-toggle-${g.member_id}`}
                      >
                        <ChevronRight size={15} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setProfileMemberId(g.member_id); setProfileOpen(true); }}
                            className="font-medium text-sm hover:underline text-left truncate"
                            data-testid={`biz-learner-link-${g.member_id}`}
                          >
                            {g.member_name}
                          </button>
                          <div className="text-[11px] text-muted-foreground truncate">{g.member_email || <span className="text-slate-300">walk-in</span>}</div>
                        </div>
                      </button>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {g.counts.expired > 0 && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">{g.counts.expired} expired</Badge>}
                        {g.counts.expiring_soon > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">{g.counts.expiring_soon} soon</Badge>}
                        {g.counts.active > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{g.counts.active} active</Badge>}
                        {total === 0 && <span className="text-[10px] text-slate-400 italic">no certificates</span>}
                      </div>
                      <div className="shrink-0 ml-2">
                        <LearnerActions
                          learner={learnerDoc}
                          onEdit={openEditLearner}
                          onUnlink={(l) => setUnlinkLearner(l)}
                          onDelete={(l) => setDeleteLearner(l)}
                          isAdmin={isAdmin}
                        />
                      </div>
                    </div>
                    {open && (
                      <div className="bg-slate-50/50 border-t border-slate-100 px-6 py-2" data-testid={`biz-learner-body-${g.member_id}`}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground text-left">
                              <th className="py-1 pr-3 font-medium">Qualification</th>
                              <th className="py-1 pr-3 font-medium">Expiry</th>
                              <th className="py-1 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map(r => {
                              const meta = STATUS_META[r.status];
                              const days = r.days_until_expiry;
                              return (
                                <tr key={r.id} className="border-t border-slate-100" data-testid={`biz-row-${r.id}`}>
                                  <td className="py-1.5 pr-3">
                                    {r.qualification_name || <span className="text-slate-300 italic">no certificates yet</span>}
                                  </td>
                                  <td className="py-1.5 pr-3">
                                    {r.expiry_date ? (
                                      <>
                                        <span>{format(parseISO(r.expiry_date), "d MMM yyyy")}</span>
                                        {days !== null && (
                                          <span className="ml-1 text-[10px] text-muted-foreground">
                                            ({days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `${days}d`})
                                          </span>
                                        )}
                                      </>
                                    ) : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="py-1.5">
                                    {meta && r.qualification_name && <Badge className={`${meta.className} text-[10px]`}>{meta.label}</Badge>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Member profile */}
      <MemberProfile
        memberId={profileMemberId}
        open={profileOpen}
        onClose={() => { setProfileOpen(false); load(); }}
        onUpdate={load}
        isAdmin={isAdmin}
      />

      {/* Edit business dialog */}
      <Dialog open={editBizOpen} onOpenChange={setEditBizOpen}>
        <DialogContent className="max-w-md" data-testid="edit-business-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Business</DialogTitle></DialogHeader>
          <form onSubmit={saveBiz} className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={bizForm.name} onChange={(e) => setBizForm({ ...bizForm, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact name</Label><Input value={bizForm.contact_name} onChange={(e) => setBizForm({ ...bizForm, contact_name: e.target.value })} /></div>
              <div><Label className="text-xs">Contact phone</Label><Input value={bizForm.contact_phone} onChange={(e) => setBizForm({ ...bizForm, contact_phone: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Contact email</Label><Input type="email" value={bizForm.contact_email} onChange={(e) => setBizForm({ ...bizForm, contact_email: e.target.value })} /></div>
            <div><Label className="text-xs">Address</Label><Input value={bizForm.address} onChange={(e) => setBizForm({ ...bizForm, address: e.target.value })} /></div>
            <div><Label className="text-xs">Website</Label><Input value={bizForm.website} onChange={(e) => setBizForm({ ...bizForm, website: e.target.value })} /></div>
            <div><Label className="text-xs">Notes</Label><Input value={bizForm.notes} onChange={(e) => setBizForm({ ...bizForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditBizOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="edit-business-save-btn">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add / edit learner */}
      <Dialog open={learnerDialogOpen} onOpenChange={setLearnerDialogOpen}>
        <DialogContent className="max-w-md" data-testid="biz-learner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingLearner
                ? "Edit Learner"
                : learnerMode === "existing"
                  ? `Add Learner to ${business.name}`
                  : `Create new learner for ${business.name}`}
            </DialogTitle>
          </DialogHeader>

          {!editingLearner && learnerMode === "existing" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Existing learner</Label>
                <SearchableSelect
                  options={allLearners
                    .filter(m => m.business_id !== id)
                    .map(m => ({
                      value: m.id,
                      label: m.name,
                      hint: m.business_id
                        ? "currently in another business"
                        : (m.email || "walk-in · no business"),
                    }))}
                  value={existingPickId}
                  onChange={setExistingPickId}
                  placeholder="Search by name or email…"
                  searchPlaceholder="Type a name or email…"
                  clearable={false}
                  testId="biz-existing-learner-select"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {(() => {
                    const picked = allLearners.find(m => m.id === existingPickId);
                    if (picked?.business_id && picked.business_id !== id) {
                      return "Heads up: this learner is currently attached to another business — attaching here will move them.";
                    }
                    return "Pick any existing learner to add them to this business.";
                  })()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLearnerMode("new")}
                className="w-full text-xs text-slate-600 hover:text-slate-900 underline decoration-dotted underline-offset-2 text-left"
                data-testid="biz-learner-switch-to-new-btn"
              >
                + Or create a brand-new learner
              </button>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setLearnerDialogOpen(false)}>Cancel</Button>
                <Button
                  type="button"
                  disabled={!existingPickId}
                  onClick={attachExistingLearner}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="biz-attach-existing-btn"
                >
                  Attach to business
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={saveLearner} className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={learnerForm.name} onChange={(e) => setLearnerForm({ ...learnerForm, name: e.target.value })} required data-testid="biz-learner-name-input" /></div>
              <div><Label className="text-xs">Email <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="email" value={learnerForm.email} onChange={(e) => setLearnerForm({ ...learnerForm, email: e.target.value })} data-testid="biz-learner-email-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Phone</Label><Input value={learnerForm.phone} onChange={(e) => setLearnerForm({ ...learnerForm, phone: e.target.value })} /></div>
                <div><Label className="text-xs">Postcode</Label><Input value={learnerForm.postcode} onChange={(e) => setLearnerForm({ ...learnerForm, postcode: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Notes</Label><Input value={learnerForm.notes} onChange={(e) => setLearnerForm({ ...learnerForm, notes: e.target.value })} /></div>
              <DialogFooter>
                {!editingLearner && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="mr-auto text-xs"
                    onClick={() => setLearnerMode("existing")}
                    data-testid="biz-learner-back-to-existing-btn"
                  >
                    ← Pick existing instead
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setLearnerDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="biz-learner-save-btn">{editingLearner ? "Update" : "Add"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete learner confirm */}
      <Dialog open={!!deleteLearner} onOpenChange={(o) => !o && setDeleteLearner(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Delete learner?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently deletes <span className="font-medium text-slate-700">{deleteLearner?.name}</span> and all of their certificate records. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLearner(null)}>Cancel</Button>
            <Button onClick={confirmDeleteLearner} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="biz-learner-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink learner confirm */}
      <Dialog open={!!unlinkLearner} onOpenChange={(o) => !o && setUnlinkLearner(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Remove from business?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-slate-700">{unlinkLearner?.name}</span> will be detached from <span className="font-medium text-slate-700">{business.name}</span> but the learner record itself stays in Members.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlinkLearner(null)}>Cancel</Button>
            <Button onClick={confirmUnlinkLearner} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="biz-learner-unlink-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete business confirm */}
      <Dialog open={deleteBizOpen} onOpenChange={setDeleteBizOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Delete business?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This detaches all linked learners from <span className="font-medium text-slate-700">{business.name}</span> (learners are not deleted) and removes the business record.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBizOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteBiz} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="business-delete-confirm-btn">Delete business</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, sublabel, value, active, onClick, tone, testId }) {
  const tones = {
    slate:   { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-900", activeBorder: "border-slate-900 ring-1 ring-slate-900" },
    rose:    { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", activeBorder: "border-rose-500 ring-1 ring-rose-500" },
    amber:   { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", activeBorder: "border-amber-500 ring-1 ring-amber-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", activeBorder: "border-emerald-500 ring-1 ring-emerald-500" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-lg border p-3 transition ${t.bg} ${active ? t.activeBorder : t.border} hover:shadow-sm`}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className={t.text}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-semibold mt-1 ${t.text}`}>{value}</div>
      {sublabel && <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{sublabel}</div>}
    </button>
  );
}

function LearnerActions({ learner, onEdit, onUnlink, onDelete, isAdmin }) {
  if (!learner) return <span className="text-xs text-slate-300">—</span>;
  return (
    <div className="inline-flex items-center gap-0.5">
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(learner)} data-testid={`biz-learner-edit-${learner.id}`} title="Edit learner">
        <Pencil size={13} />
      </Button>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900" onClick={() => onUnlink(learner)} data-testid={`biz-learner-unlink-${learner.id}`} title="Remove from business">
        <UserMinus size={13} />
      </Button>
      {isAdmin && (
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" onClick={() => onDelete(learner)} data-testid={`biz-learner-delete-${learner.id}`} title="Delete learner">
          <Trash2 size={13} />
        </Button>
      )}
    </div>
  );
}
